import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FormBlock } from '@qbe/ads-blocks-forms';
import { Button, FormGroupLabel, Typography } from '@qbe/ads-core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { CoverSelectorGrid, CoverSelectorGridItem } from '~/components/CoverSelector/Layout';
import Modal from '~/components-ds/Modal';
import { useAppContent, useAppDispatch, useOptionSelect, useSelectCover } from '~/hooks';
import { useAppNavigate } from '~/hooks/useAppNavigate';
import { useReviewAndPay } from '~/hooks/useReviewAndPay';
import { addAdditionalCovers, removeAdditionalCovers } from '~/slices/reviewAndPay';
import { addCover } from '~/slices/selectCover';
import { qalFormStartAction, qalTrackEventAction } from '~/thunks/analytics';
import { navigateAction } from '~/thunks/navigateAction';
import { Cover } from '~/types/cover';
import { coverToRoute, isNotEmptyObjOrArray } from '~/utils';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';

import { CoverOption } from '../CoverSelector/CoverOption';

interface ReviewCoversProps {
  options: Cover[];
  openWarningModal: boolean;
  onClose: () => void;
  handleContinueWithoutAdding: () => void;
}

const ReviewCovers: React.FC<ReviewCoversProps> = ({
  options,
  openWarningModal,
  onClose,
  handleContinueWithoutAdding,
}) => {
  const { getTextContent } = useAppContent({ basePath: 'dsme.review' });
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const { completedCovers } = useSelectCover();
  const { additionalCovers } = useReviewAndPay() as { additionalCovers: Cover[] };
  const [coverOptions, setCoverOptions] = useState(options);
  const { selectedIDs, onSelectID, setSelectedIDs } = useOptionSelect({
    options: coverOptions.map((id) => ({
      id,
      value: id,
    })),
    multiSelect: true,
  });

  const handleFormStart = useCallback(() => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  }, [dispatch]);

  useEffect(() => {
    setCoverOptions(options);
  }, [options]);

  const handleAdd = () => {
    additionalCovers.forEach((id) => {
      handleFormStart();
      dispatch(
        qalTrackEventAction({
          event: 'submit',
          eventAction: 'Add Cover',
          eventLabel: id,
        })
      );
      dispatch(addCover(id));
    });
    dispatch(
      navigateAction({
        to: coverToRoute[additionalCovers[0]],
        navigate,
      })
    );
  };

  const optionPropsSelector = useMemo(() => {
    const handleSelect = (id: Cover) => {
      handleFormStart();
      if (!selectedIDs.includes(id)) {
        dispatch(addAdditionalCovers(id));
      } else {
        dispatch(removeAdditionalCovers(id));

        /* When we unselect BuildingAndContents -> unselect BI as well */
        if (id === Cover.BuildingAndContents && selectedIDs.includes(Cover.BusinessInterruption)) {
          dispatch(removeAdditionalCovers(Cover.BusinessInterruption));
          setSelectedIDs(
            selectedIDs.filter(
              (s) => ![Cover.BuildingAndContents, Cover.BusinessInterruption].includes(s as Cover)
            )
          );
          return;
        }
      }

      onSelectID(id);
    };

    return coverOptions.map((coverId) => {
      const isSelected = !!selectedIDs.find((o) => o === coverId);
      const isBICoverDisabled =
        !selectedIDs.includes(Cover.BuildingAndContents) &&
        !completedCovers.includes(Cover.BuildingAndContents) &&
        coverId === Cover.BusinessInterruption;

      return {
        id: coverId,
        isSelected,
        handleSelect,
        disabled: isBICoverDisabled,
      };
    });
  }, [
    coverOptions,
    handleFormStart,
    selectedIDs,
    onSelectID,
    dispatch,
    setSelectedIDs,
    completedCovers,
  ]);

  return (
    <>
      <FormGroupLabel title={getTextContent('fields.covers.title')} alignCenter />

      <CoverSelectorGrid>
        {optionPropsSelector.map((props) => (
          <CoverSelectorGridItem key={props.id}>
            <CoverOption {...props} />
          </CoverSelectorGridItem>
        ))}
      </CoverSelectorGrid>

      <FormBlock preset="button">
        <Button
          label={getTextContent('buttons.coversAdd')}
          data-track={getTextContent('buttons.coversAdd')}
          onClick={handleAdd}
          disabled={!isNotEmptyObjOrArray(additionalCovers)}
        />
      </FormBlock>

      {openWarningModal && (
        <Modal
          id="cover-warning-modal"
          open
          title={getTextContent('fields.coversWarning.title')}
          analyticsModalName="cover warning modal"
          onClose={onClose}
          iconProps={{
            muiIcon: InfoOutlinedIcon,
          }}
          primaryBtnProps={
            {
              label: getTextContent('buttons.coversWarningPrimary'),
              onClick: handleAdd,
              'data-track': 'Add covers - warning modal',
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any
          }
          secondaryBtnProps={
            {
              label: getTextContent('buttons.coversWarningSecondary'),
              'data-track': 'Continue without adding covers - warning modal',
              onClick: handleContinueWithoutAdding,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any
          }
        >
          <Typography>{getTextContent('fields.coversWarning.description')}</Typography>
        </Modal>
      )}
    </>
  );
};

export default ReviewCovers;
